import { useEffect, useRef, useState } from "react";

type UseCheckoutCtaScrollArgs = {
    /** When false no listener attaches and visibility stays false — keeps the control group inert. */
    enabled: boolean;
    /** The step's real footer CTA (`appState.formContinueButtonRef`); the test CTA hides once it's on screen. */
    footerCtaRef?: React.RefObject<HTMLButtonElement | null>;
};

/** Dead band (px) either side of every boundary, so momentum-scroll jitter can't flip the CTA repeatedly. */
const BOUNDARY_BAND = 8;

/**
 * ENG-17301 — drives the coverage-step "Proceed to Checkout" test CTA (both test variants).
 *
 * The CTA is on screen inside a window: once the coverage card has scrolled off the top of the
 * viewport, and until *either* closing edge is reached — the preventive care card scrolling off the
 * top in its turn (the ticket's wording, "scrolls past the preventive care card") or the step's own
 * footer CTA coming fully into view (the ticket's parenthetical gloss, "once the checkout button at
 * the bottom of the screen"). They are not the same point, since the exclusions and disclaimer
 * blocks sit between them, so whichever comes first wins; the footer check is the backstop that
 * guarantees the test CTA is never on screen next to the real one — including when preventive care
 * renders nothing at all.
 *
 * Every boundary is a "has this box passed the viewport *top*" test, never a "is this box on
 * screen" test, because the second kind is viewport-height dependent in a way that silently voids
 * the test on desktop: at the frame the coverage card clears the top, the preventive card sits
 * directly below it, so any viewport tall enough to show that card whole met the old closing
 * condition on the same frame that opened the window and the CTA never appeared. The one edge that
 * must stay viewport-relative is the footer CTA, since it is about the real button being reachable.
 *
 * Reads `getBoundingClientRect()` from a rAF-throttled scroll/resize listener rather than using
 * `useIntersectionObserver`, because an observer reports nothing at all for a box already outside
 * the root when it mounts — a restored scroll position, or a coverage card taller than the viewport
 * — which would strand the CTA in whichever state it started in. A `ResizeObserver` on the document
 * element covers content that grows after mount (pet plans resolving, preventive cards expanding)
 * without a scroll event to trigger a re-measure; it can't feedback-loop because both CTAs are
 * `fixed`/portalled and so never change document height.
 */
export function useCheckoutCtaScroll({ enabled, footerCtaRef }: UseCheckoutCtaScrollArgs) {
    /** Attach to the coverage card's own wrapper — the CTA shows once its bottom passes the viewport top. */
    const coverageEndRef = useRef<HTMLDivElement>(null);
    /** Attach to a wrapper around the preventive care card — the CTA hides once its bottom passes the viewport top too. */
    const preventiveEndRef = useRef<HTMLDivElement>(null);
    const [isCheckoutCtaVisible, setIsCheckoutCtaVisible] = useState(false);
    // Mirrors state so `measure` can apply the dead band against the live value without
    // re-subscribing the listener on every crossing.
    const isVisibleRef = useRef(false);

    useEffect(() => {
        if (!enabled) {
            isVisibleRef.current = false;
            return;
        }

        let frame: number | null = null;

        const commit = (nextVisible: boolean) => {
            isVisibleRef.current = nextVisible;
            setIsCheckoutCtaVisible(nextVisible);
        };

        const measure = () => {
            frame = null;

            const coverageEnd = coverageEndRef.current;
            // The coverage subtree unmounts while pet plans refetch. Hide rather than bail out, or
            // the CTA stays pinned over the loader with nothing left to measure against.
            if (!coverageEnd) {
                commit(false);
                return;
            }

            // Every threshold below is offset by the dead band in whichever direction holds the
            // current state: a box has to be clearly past its boundary to change the CTA, and
            // clearly back on the other side to change it again.
            const band = isVisibleRef.current ? BOUNDARY_BAND : -BOUNDARY_BAND;

            const hasPassedCoverage = coverageEnd.getBoundingClientRect().bottom <= band;

            // PreventiveCare renders null when a pet has no options, and its wrapper is `empty:hidden`
            // — a `display: none` box reports an all-zeros rect, so the height check keeps that from
            // reading as "already scrolled past" and suppressing the CTA outright.
            const preventiveRect = preventiveEndRef.current?.getBoundingClientRect();
            const hasPassedPreventive = !!preventiveRect && preventiveRect.height > 0 && preventiveRect.bottom <= -band;

            // Fully in view, not merely touching the bottom edge: the guard exists to stop two
            // identical CTAs sharing the screen, which the top edge crossing over doesn't yet mean.
            // The height check covers a `hideContinueButton` step (and `isConnected` a stale ref):
            // either way the rect is all zeros, which would read as "footer CTA on screen" and
            // suppress the test CTA forever.
            const footerCta = footerCtaRef?.current;
            const footerRect = footerCta?.isConnected ? footerCta.getBoundingClientRect() : undefined;
            const isFooterCtaInView = !!footerRect && footerRect.height > 0 && footerRect.bottom <= window.innerHeight - band;

            commit(hasPassedCoverage && !hasPassedPreventive && !isFooterCtaInView);
        };

        const schedule = () => {
            if (frame !== null) return;
            frame = window.requestAnimationFrame(measure);
        };

        // Scheduled, not called straight through: `measure` commits state, and running it synchronously
        // in the effect body is the cascading-render pattern. The first measurement therefore lands on
        // the next animation frame — the CTA already fades in, so the extra frame is invisible.
        schedule();

        window.addEventListener("scroll", schedule, { passive: true });
        window.addEventListener("resize", schedule);

        const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : undefined;
        resizeObserver?.observe(document.documentElement);

        return () => {
            if (frame !== null) window.cancelAnimationFrame(frame);
            window.removeEventListener("scroll", schedule);
            window.removeEventListener("resize", schedule);
            resizeObserver?.disconnect();
        };
    }, [enabled, footerCtaRef]);

    // `enabled` gates visibility by derivation rather than by resetting the measured state: the control
    // group stays inert without the disabled branch of the effect having to write state.
    return { coverageEndRef, preventiveEndRef, isCheckoutCtaVisible: enabled && isCheckoutCtaVisible };
}
